import React, { useEffect, useMemo, useState } from 'react';
import { CancelPipelineButton } from './CancelPipelineButton';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Tooltip, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup, Drawer, Tabs, Tab, Button, Divider, Avatar,
  CircularProgress, Alert, List, ListItem, LinearProgress, Collapse, Grid,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Search as SearchIcon, Clear as ClearIcon, Apple, Android, OpenInNew,
  Close as CloseIcon, ContentCopy, CheckCircle, ErrorOutline, HourglassEmpty,
  Cancel, Timer, FilterAltOutlined,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { dataGridSx, dataGridContainerSx } from '../utils/tableStyles';

interface BuildsListComponentProps { isActive: boolean }

type StatusFilter = 'all' | 'success' | 'failed' | 'running';

type BuildRow = {
  id: string | number;
  pipelineId?: number | string;
  composerHost: string;
  appId?: number | string;
  version: string;
  platform: string;
  status: string;
  phase?: string;
  submitter: string;
  timestamp: string;
  pipelineUrl?: string;
  raw: any; // the original recentBuilds entry for the drawer "all fields" tab
};

const platformIcon = (p: string) => {
  const pl = (p || '').toLowerCase();
  if (pl === 'ios') return <Apple sx={{ fontSize: 14 }} />;
  if (pl === 'android') return <Android sx={{ fontSize: 14 }} />;
  return null;
};

const statusMeta = (status: string) => {
  const s = (status || '').toLowerCase();
  if (/success/.test(s)) return { label: 'Success', color: '#16a34a', bg: 'rgba(22, 163, 74, 0.12)', icon: <CheckCircle sx={{ fontSize: 13 }} /> };
  if (/cancel/.test(s)) return { label: 'Cancelled', color: '#b45309', bg: 'rgba(245, 158, 11, 0.15)', icon: <Cancel sx={{ fontSize: 13 }} /> };
  if (/fail|timeout|stuck/.test(s)) return { label: 'Failed', color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)', icon: <ErrorOutline sx={{ fontSize: 13 }} /> };
  if (/queue|running|pending/.test(s)) return { label: 'Running', color: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.12)', icon: <HourglassEmpty sx={{ fontSize: 13 }} /> };
  return { label: status || 'Unknown', color: '#475569', bg: 'rgba(15, 23, 42, 0.06)', icon: null };
};

function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
}

export const BuildsListComponent: React.FC<BuildsListComponentProps> = ({ isActive }) => {
  const { dashboardData, fetchDashboardData, isLoadingAppData } = useAuth();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Drawer state
  const [drawerBuild, setDrawerBuild] = useState<BuildRow | null>(null);
  const [drawerTab, setDrawerTab] = useState<0 | 1 | 2>(0);
  const [drawerDetails, setDrawerDetails] = useState<any | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);

  // Snackbar
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  // Lazy-load dashboard data if landed direct on this page
  useEffect(() => {
    if (isActive && (!dashboardData?.recentBuilds || dashboardData.recentBuilds.length === 0)) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Adaptive refresh while open: every 8 s if any pipeline in the current
  // view is still in flight (queued / running / pending) so live statuses
  // update without the user clicking anything, and back to ~35 s when
  // everything is terminal so we don't pound the dashboard endpoint at idle.
  const hasInFlight = useMemo(
    () => (dashboardData?.recentBuilds || []).some((b: any) =>
      /queue|running|pending/i.test(b.status || b.pipelineStatus || '')
    ),
    [dashboardData],
  );
  useEffect(() => {
    if (!isActive) return;
    const interval = hasInFlight ? 8000 : 35000 + Math.random() * 5000;
    const t = setInterval(fetchDashboardData, interval);
    return () => clearInterval(t);
  }, [isActive, fetchDashboardData, hasInFlight]);

  // Refresh the moment the tab/window regains focus, so an operator who
  // alt-tabbed away during a deploy sees the latest status the second they
  // come back instead of waiting up to 8 s for the next poll tick.
  useEffect(() => {
    if (!isActive) return;
    const onVisible = () => { if (document.visibilityState === 'visible') fetchDashboardData(); };
    const onFocus = () => fetchDashboardData();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [isActive, fetchDashboardData]);

  // Map every recentBuild to a uniform row shape; keep the original payload on
  // `raw` so the drawer can show every field the API returned without us
  // hard-coding a list.
  const rows: BuildRow[] = useMemo(() => {
    const src = (dashboardData?.recentBuilds || []) as any[];
    return src.map((b) => ({
      id: String(b.id ?? b.pipelineId ?? `${b.composerHost}-${b.dateTimeStamp || b.updatedAt || Math.random()}`),
      pipelineId: b.pipelineId,
      composerHost: b.composerHost || b.domain || 'Unknown',
      appId: b.appId,
      version: b.version || '—',
      platform: (b.osSpecification || b.platform || '').toLowerCase(),
      status: b.status || b.pipelineStatus || 'unknown',
      phase: b.phase,
      submitter: b.submitter || b.user || 'N/A',
      timestamp: b.dateTimeStamp || b.updatedAt || b.createdAt || '',
      pipelineUrl: b.pipelineUrl || b.gitlabPipelineUrl,
      raw: b,
    }));
  }, [dashboardData]);

  // Client-side filter — search + status. Both reuse the same data, no extra API.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== 'all') {
        const s = (r.status || '').toLowerCase();
        if (statusFilter === 'success' && !/success/.test(s)) return false;
        if (statusFilter === 'failed' && !/fail|cancel|timeout|stuck/.test(s)) return false;
        if (statusFilter === 'running' && !/queue|running|pending/.test(s)) return false;
      }
      if (!q) return true;
      return (
        (r.composerHost || '').toLowerCase().includes(q) ||
        String(r.pipelineId || '').toLowerCase().includes(q) ||
        (r.version || '').toLowerCase().includes(q) ||
        (r.submitter || '').toLowerCase().includes(q) ||
        (r.platform || '').toLowerCase().includes(q) ||
        (r.phase || '').toLowerCase().includes(q) ||
        (r.status || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter]);

  const counts = useMemo(() => ({
    all: rows.length,
    success: rows.filter(r => /success/i.test(r.status)).length,
    failed: rows.filter(r => /fail|cancel|timeout|stuck/i.test(r.status)).length,
    running: rows.filter(r => /queue|running|pending/i.test(r.status)).length,
  }), [rows]);

  // -------------------- Drawer logic --------------------
  const openDrawer = async (row: BuildRow) => {
    setDrawerBuild(row);
    setDrawerTab(0);
    setDrawerDetails(null);
    setDrawerError(null);
    // Fetch /api/builds/:id for the full record + phases. Same endpoint
    // the Error Logs page uses — no new API.
    setDrawerLoading(true);
    try {
      const tok = localStorage.getItem('token');
      const resp = await axios.get(`/api/builds/${encodeURIComponent(String(row.id))}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      setDrawerDetails(resp.data || null);
    } catch (err: any) {
      setDrawerError(err?.response?.data?.error || 'Failed to load full build details');
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerBuild(null);
    setDrawerDetails(null);
    setDrawerError(null);
  };

  const copyToClipboard = (value: string) => {
    const flash = (msg: string) => { setCopyMsg(msg); setTimeout(() => setCopyMsg(null), 1500); };
    // navigator.clipboard is undefined on insecure origins / older browsers —
    // the optional-chained call then returns undefined, so guard before .then().
    const p = navigator.clipboard?.writeText(value);
    if (!p) { flash('Copy failed'); return; }
    p.then(() => flash('Copied')).catch(() => flash('Copy failed'));
  };

  // Format an ISO/date string for the detail drawer, tolerating null/empty and
  // unparseable values (date-fns v2 throws RangeError on an invalid Date).
  const safeDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : format(d, 'PPpp');
  };

  // -------------------- Columns --------------------
  const columns: GridColDef[] = [
    {
      field: 'pipelineId',
      headerName: 'Build / Pipeline',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const r = params.row as BuildRow;
        return (
          <Stack direction="column" alignItems="center" sx={{ lineHeight: 1.2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
              #{String(r.id)}
            </Typography>
            {r.pipelineId && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                pipe {String(r.pipelineId)}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'composerHost',
      headerName: 'App / Host',
      flex: 1,
      minWidth: 240,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const r = params.row as BuildRow;
        const isIOS = r.platform === 'ios';
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
            <Avatar sx={{ bgcolor: isIOS ? '#0f172a' : '#3DDC84', width: 32, height: 32, flexShrink: 0 }}>
              {isIOS ? <Apple sx={{ fontSize: 18 }} /> : <Android sx={{ fontSize: 18 }} />}
            </Avatar>
            <Box sx={{ minWidth: 0, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25 }}>
                {r.composerHost}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                v{r.version} · {isIOS ? 'iOS' : r.platform === 'android' ? 'Android' : r.platform || '—'}
              </Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const meta = statusMeta((params.row as BuildRow).status);
        const phase = (params.row as BuildRow).phase;
        return (
          <Stack direction="column" alignItems="center" spacing={0.25}>
            <Chip
              size="small"
              icon={meta.icon || undefined}
              label={meta.label}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: meta.bg,
                color: meta.color,
                '& .MuiChip-icon': { color: meta.color, ml: 0.5 },
              }}
            />
            {meta.label === 'Running' && phase && (
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                {phase.replace(/_/g, ' ')}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    { field: 'submitter', headerName: 'Submitter', width: 160, align: 'center', headerAlign: 'center' },
    {
      field: 'timestamp',
      headerName: 'Time',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const t = (params.row as BuildRow).timestamp;
        if (!t) return <Typography variant="caption" color="text.disabled">—</Typography>;
        const d = new Date(t);
        if (Number.isNaN(d.getTime())) return <Typography variant="caption" color="text.disabled">—</Typography>;
        return (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {format(d, 'MMM dd, HH:mm')}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 104,
      minWidth: 104,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const r = params.row as BuildRow;
        const isRunning = /queue|running|pending/i.test(r.status || '');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 0.5 }}>
            {isRunning && (
              <CancelPipelineButton
                buildId={r.id}
                label={r.composerHost}
                version={r.version}
                platform={r.platform}
                onCancelled={() => fetchDashboardData()}
              />
            )}
            <Tooltip title="View full build details" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); openDrawer(r); }}
                sx={{
                  color: 'primary.main',
                  bgcolor: 'rgba(99, 102, 241, 0.08)',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.16)' },
                }}
              >
                <OpenInNew sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const phases = useMemo<any[]>(() => drawerDetails?.phases || [], [drawerDetails]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
            Build Browser
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Every build from the recent activity feed — click any row to open the full per-build detail drawer.
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1.5 }}>
          <FilterAltOutlined sx={{ color: 'text.secondary' }} />
          <ToggleButtonGroup
            data-tour="builds-filter"
            size="small"
            exclusive
            value={statusFilter}
            onChange={(_, v) => v && setStatusFilter(v)}
            sx={{
              height: 40,
              '& .MuiToggleButton-root': {
                textTransform: 'none', px: 1.5, fontWeight: 600, border: '1px solid #e2e8f0',
                '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#4338ca', borderColor: '#6366f1', zIndex: 1 },
              },
            }}
          >
            <ToggleButton value="all">All · {counts.all}</ToggleButton>
            <ToggleButton value="success">Success · {counts.success}</ToggleButton>
            <ToggleButton value="failed">Failed · {counts.failed}</ToggleButton>
            <ToggleButton value="running">Running · {counts.running}</ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          <TextField
            size="small"
            placeholder="Search by host, pipeline, version, submitter, phase…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {(search || statusFilter !== 'all') && (
            <Typography variant="caption" color="text.secondary">
              {filteredRows.length} of {rows.length}
            </Typography>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ ...dataGridContainerSx, height: 650 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoadingAppData && filteredRows.length === 0}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          rowHeight={60}
          disableRowSelectionOnClick
          onRowClick={(params) => openDrawer(params.row as BuildRow)}
          sx={dataGridSx}
        />
      </Paper>

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={!!drawerBuild}
        onClose={closeDrawer}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(15, 23, 42, 0.3)' } } }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, borderRadius: 0 } }}
      >
        {drawerBuild && (() => {
          const meta = statusMeta(drawerBuild.status);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${meta.color}1f`, color: meta.color,
                  }}>
                    {meta.icon || <HourglassEmpty />}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                      Build #{String(drawerBuild.id)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {drawerBuild.composerHost} · v{drawerBuild.version} · {drawerBuild.platform || '—'}
                    </Typography>
                  </Box>
                </Stack>
                <IconButton onClick={closeDrawer} size="small"><CloseIcon /></IconButton>
              </Box>

              <Divider />
              <Tabs
                value={drawerTab}
                onChange={(_, v) => setDrawerTab(v)}
                sx={{ px: 2, borderBottom: '1px solid #e2e8f0' }}
              >
                <Tab label="Overview" sx={{ textTransform: 'none', fontWeight: 700 }} />
                <Tab label="Phase timing" icon={<Timer sx={{ fontSize: 16 }} />} iconPosition="end" sx={{ textTransform: 'none', fontWeight: 700 }} />
                <Tab label="Raw payload" sx={{ textTransform: 'none', fontWeight: 700 }} />
              </Tabs>

              {/* Overview */}
              {drawerTab === 0 && (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      size="small"
                      icon={meta.icon || undefined}
                      label={meta.label}
                      sx={{ fontWeight: 700, bgcolor: meta.bg, color: meta.color, '& .MuiChip-icon': { color: meta.color, ml: 0.5 } }}
                    />
                    {drawerBuild.phase && (
                      <Chip size="small" label={drawerBuild.phase.replace(/_/g, ' ')} sx={{ fontWeight: 700, textTransform: 'capitalize', bgcolor: 'rgba(15, 23, 42, 0.06)' }} />
                    )}
                    {drawerBuild.platform && (
                      <Chip
                        size="small"
                        icon={platformIcon(drawerBuild.platform) || undefined}
                        label={drawerBuild.platform === 'ios' ? 'iOS' : drawerBuild.platform === 'android' ? 'Android' : drawerBuild.platform}
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  <DetailGrid
                    items={[
                      { label: 'Build ID', value: String(drawerBuild.id), mono: true },
                      { label: 'Pipeline ID', value: drawerBuild.pipelineId ? String(drawerBuild.pipelineId) : '—', mono: true },
                      { label: 'App ID', value: drawerBuild.appId ? String(drawerBuild.appId) : '—', mono: true },
                      { label: 'Composer Host', value: drawerBuild.composerHost },
                      { label: 'Version', value: `v${drawerBuild.version}`, mono: true },
                      { label: 'Platform', value: drawerBuild.platform || '—' },
                      { label: 'Status', value: drawerBuild.status },
                      { label: 'Submitter', value: drawerBuild.submitter, breakAll: true },
                      { label: 'Triggered at', value: safeDateTime(drawerBuild.timestamp) },
                    ]}
                    onCopy={copyToClipboard}
                  />

                  {drawerLoading && (
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">Loading enriched build details…</Typography>
                    </Stack>
                  )}
                  {drawerError && !drawerLoading && (
                    <Alert severity="warning" sx={{ mt: 3 }}>{drawerError}</Alert>
                  )}
                  {drawerDetails && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Pipeline metadata</Typography>
                      <DetailGrid
                        items={[
                          { label: 'GitLab pipeline ID', value: drawerDetails.gitlabPipelineId ? String(drawerDetails.gitlabPipelineId) : '—', mono: true },
                          { label: 'GitLab pipeline URL', value: drawerDetails.gitlabPipelineUrl || '—', link: drawerDetails.gitlabPipelineUrl },
                          { label: 'Trigger source', value: drawerDetails.triggerSource || '—' },
                          { label: 'Batch ID', value: drawerDetails.batchId || '—', mono: true },
                          { label: 'Code version', value: drawerDetails.codeVersion || '—', mono: true },
                          { label: 'Changelog', value: drawerDetails.changelog || '—', breakAll: true },
                          { label: 'Queued at', value: safeDateTime(drawerDetails.queuedAt) },
                          { label: 'Started at', value: safeDateTime(drawerDetails.startedAt) },
                          { label: 'Finished at', value: safeDateTime(drawerDetails.finishedAt) },
                        ]}
                        onCopy={copyToClipboard}
                      />
                      {drawerDetails.failureReason && (
                        <>
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mt: 2 }}>Failure reason</Typography>
                          <Box sx={{
                            bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 2, p: 2,
                            fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.5,
                            maxHeight: 240, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {drawerDetails.failureReason}
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* Phase timing */}
              {drawerTab === 1 && (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  {drawerLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                  )}
                  {!drawerLoading && phases.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No phase data captured for this build.</Typography>
                  )}
                  {!drawerLoading && phases.length > 0 && (() => {
                    const completed = phases.filter(p => p.startedAt && p.finishedAt);
                    const maxDuration = completed.reduce((m, p) => Math.max(m, new Date(p.finishedAt).getTime() - new Date(p.startedAt).getTime()), 0);
                    const totalDuration = completed.reduce((sum, p) => sum + (new Date(p.finishedAt).getTime() - new Date(p.startedAt).getTime()), 0);
                    return (
                      <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <Chip label={`Total: ${formatDuration(totalDuration)}`} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(99, 102, 241, 0.12)', color: '#4338ca' }} />
                          {maxDuration > 0 && <Chip label={`Slowest: ${formatDuration(maxDuration)}`} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#92400e' }} />}
                        </Stack>
                        <List dense disablePadding>
                          {phases.map((p, i) => {
                            const dur = p.startedAt && p.finishedAt ? new Date(p.finishedAt).getTime() - new Date(p.startedAt).getTime() : null;
                            const pct = dur && maxDuration > 0 ? (dur / maxDuration) * 100 : 0;
                            const ps = (p.status || '').toLowerCase();
                            const c = /success/.test(ps) ? '#16a34a' : /fail/.test(ps) ? '#dc2626' : /cancel/.test(ps) ? '#f59e0b' : /running/.test(ps) ? '#3b82f6' : '#94a3b8';
                            const isSlowest = dur === maxDuration && maxDuration > 0;
                            return (
                              <ListItem key={`${p.id || i}-${p.phase}`} disableGutters sx={{
                                flexDirection: 'column', alignItems: 'stretch', mb: 1,
                                border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5,
                                ...(isSlowest && { borderColor: 'rgba(245, 158, 11, 0.5)', backgroundColor: 'rgba(245, 158, 11, 0.06)' }),
                              }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: c, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                                    {(p.sequence ?? i) + 1}
                                  </Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, textTransform: 'uppercase' }}>
                                    {(p.phase || 'unknown').replace(/_/g, ' ')}
                                  </Typography>
                                  <Chip size="small" label={ps || 'pending'} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${c}1a`, color: c, textTransform: 'capitalize' }} />
                                  <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 56, textAlign: 'right' }}>
                                    {dur !== null ? formatDuration(dur) : '—'}
                                  </Typography>
                                </Stack>
                                {dur !== null && (
                                  <LinearProgress variant="determinate" value={pct} sx={{
                                    height: 6, borderRadius: 1, bgcolor: '#f1f5f9',
                                    '& .MuiLinearProgress-bar': { bgcolor: c },
                                  }} />
                                )}
                                {p.errorMessage && (
                                  <Collapse in={true}>
                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(220, 38, 38, 0.06)', borderRadius: 1, fontSize: '0.75rem', fontFamily: 'monospace', color: '#991b1b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {p.errorMessage}
                                    </Box>
                                  </Collapse>
                                )}
                                {p.gitlabJobUrl && (
                                  <Button
                                    size="small" href={p.gitlabJobUrl} target="_blank" rel="noopener noreferrer"
                                    startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                                    sx={{ alignSelf: 'flex-start', mt: 0.75, textTransform: 'none', fontWeight: 600 }}
                                  >
                                    GitLab job
                                  </Button>
                                )}
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {/* Raw payload */}
              {drawerTab === 2 && (
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Full API response
                    </Typography>
                    <Tooltip title="Copy JSON" arrow>
                      <IconButton size="small" onClick={() => copyToClipboard(JSON.stringify(drawerDetails ?? drawerBuild.raw, null, 2))}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box sx={{
                    bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 2, p: 2,
                    fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.55,
                    maxHeight: 'calc(100vh - 320px)', overflow: 'auto',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {JSON.stringify(drawerDetails ?? drawerBuild.raw, null, 2)}
                  </Box>
                </Box>
              )}

              <Divider />
              <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={closeDrawer} color="inherit">Close</Button>
                {(() => {
                  // The feed row often lacks the pipeline URL; it arrives with
                  // the detail fetch (drawerDetails.gitlabPipelineUrl). Fall back
                  // to it so the button isn't permanently disabled.
                  const jobUrl = drawerBuild.pipelineUrl || drawerDetails?.gitlabPipelineUrl;
                  return (
                    <Button
                      variant="contained"
                      startIcon={<OpenInNew />}
                      disabled={!jobUrl}
                      onClick={() => jobUrl && window.open(jobUrl, '_blank', 'noopener,noreferrer')}
                    >
                      Open GitLab Job
                    </Button>
                  );
                })()}
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      {copyMsg && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, bgcolor: '#0f172a', color: '#fff', px: 2, py: 1, borderRadius: 2, zIndex: 1400, fontSize: '0.85rem' }}>
          {copyMsg}
        </Box>
      )}
    </Box>
  );
};

// Generic detail grid — renders a label/value list with optional copy and
// link affordances. Reused by both the Overview tab and the metadata block.
const DetailGrid: React.FC<{
  items: Array<{ label: string; value: string; mono?: boolean; breakAll?: boolean; link?: string }>;
  onCopy?: (value: string) => void;
}> = ({ items, onCopy }) => (
  <Grid container spacing={1.5}>
    {items.map((it) => (
      <Grid item xs={12} sm={6} key={it.label}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
          {it.label}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {it.link && it.value !== '—' ? (
            <Typography
              component="a"
              href={it.link}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{
                fontWeight: 600, color: 'primary.main', textDecoration: 'none',
                fontFamily: it.mono ? 'monospace' : 'inherit',
                overflowWrap: it.breakAll ? 'anywhere' : 'normal',
              }}
            >
              {it.value}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: it.value === '—' ? 'text.secondary' : '#0f172a',
                fontFamily: it.mono ? 'monospace' : 'inherit',
                overflowWrap: it.breakAll ? 'anywhere' : 'normal',
              }}
            >
              {it.value}
            </Typography>
          )}
          {onCopy && it.value !== '—' && (
            <Tooltip title="Copy" arrow>
              <IconButton size="small" onClick={() => onCopy(it.value)} sx={{ p: 0.25 }}>
                <ContentCopy sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Grid>
    ))}
  </Grid>
);

export default BuildsListComponent;
